import { z } from 'zod';

/**
 * Every interval in the system, with production defaults from the brief. Each can be
 * overridden by its env var so E2E tests run in seconds (pattern from Mantle Watch).
 * Production deployments must not set these.
 */
export const TIMING_DEFAULTS = {
  otpTtlSec: 600,
  otpMaxAttempts: 5,
  otpResendCooldownSec: 60,
  /** Wrong email codes per address over a rolling window before it locks (Keycloak SPI). */
  otpMaxFailures: 10,
  otpFailureWindowSec: 86_400,
  accessTokenTtlSec: 300,
  ssoIdleSec: 1800,
  offlineIdleSec: 2_592_000,
  permissionCacheSec: 30,
  healthPollSec: 60,
  signatureMaxSkewSec: 60,
  nonceTtlSec: 300,
  directoryMaxAgeSec: 300,
  impersonationTtlSec: 1800,
  /** tenant-api asks central for Expo push receipts this often (Expo keeps them ~24 h). */
  pushReceiptsPollSec: 900,
  /** tenant-api removes archived notifications past the school's retention this often (6 h). */
  notificationTidySec: 21_600,
  /** tenant-api prunes push devices this often (daily; D16, review S12). */
  devicePruneSec: 86_400,
  /** A push device unused (not re-registered at launch) this long is removed: 60 days. */
  deviceMaxIdleSec: 5_184_000,
} as const;

export type TimingKey = keyof typeof TIMING_DEFAULTS;
export type Timings = { readonly [K in TimingKey]: number };

export const TIMING_ENV: { readonly [K in TimingKey]: string } = {
  otpTtlSec: 'TP_OTP_TTL_SEC',
  otpMaxAttempts: 'TP_OTP_MAX_ATTEMPTS',
  otpResendCooldownSec: 'TP_OTP_RESEND_COOLDOWN_SEC',
  otpMaxFailures: 'TP_OTP_MAX_FAILURES',
  otpFailureWindowSec: 'TP_OTP_FAILURE_WINDOW_SEC',
  accessTokenTtlSec: 'TP_ACCESS_TOKEN_TTL_SEC',
  ssoIdleSec: 'TP_SSO_IDLE_SEC',
  offlineIdleSec: 'TP_OFFLINE_IDLE_SEC',
  permissionCacheSec: 'TP_PERMISSION_CACHE_SEC',
  healthPollSec: 'TP_HEALTH_POLL_SEC',
  signatureMaxSkewSec: 'TP_SIGNATURE_MAX_SKEW_SEC',
  nonceTtlSec: 'TP_NONCE_TTL_SEC',
  directoryMaxAgeSec: 'TP_DIRECTORY_MAX_AGE_SEC',
  impersonationTtlSec: 'TP_IMPERSONATION_TTL_SEC',
  pushReceiptsPollSec: 'TP_PUSH_RECEIPTS_POLL_SEC',
  notificationTidySec: 'TP_NOTIFICATION_TIDY_SEC',
  devicePruneSec: 'TP_DEVICE_PRUNE_SEC',
  deviceMaxIdleSec: 'TP_DEVICE_MAX_IDLE_SEC',
};

const positiveInt = z.coerce.number().int().positive();

/** Reads overrides from env; invalid values fail fast with the variable name. */
export function resolveTimings(env: Readonly<Record<string, string | undefined>>): Timings {
  const entries = (Object.keys(TIMING_DEFAULTS) as TimingKey[]).map((key) => {
    const name = TIMING_ENV[key];
    const raw = env[name];
    if (raw === undefined || raw === '') return [key, TIMING_DEFAULTS[key]] as const;
    const parsed = positiveInt.safeParse(raw);
    if (!parsed.success) throw new Error(`${name} must be a positive integer (got "${raw}")`);
    return [key, parsed.data] as const;
  });
  return Object.freeze(Object.fromEntries(entries)) as Timings;
}
